/*
<summary>
  This component to handle viewing, adding, and deleting store motivations.
  Developer: Sahil Bharti | Date: 14-April-2025
</summary>
<returns>JSX</returns>
*/
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../contexts/store-provider";
import { formatMessage } from "../../translations/format-message";
import DeletePopup from "../../shared-components/popup/delete/delete-popup";
import MotivationInStoreListView from "./components/motivation-in-store-list-view";
import DeletePopupHOC from "../../shared-components/popup/delete/delete-popup-hoc";
import { getStoreIdFromLocalStorage } from "../../helpers/localstorage-helper";
import FormLoader from "../../shared-components/FormLoader/FormLoader";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const MotivationInStore = (props: IProps) => {
  const { masterManagementStore } = useStore();
  const {
    addUpdateMotivationInStoreState,
    GetStoreMotivationByStoreId,
    AddStoreMotivation,
    DeleteStoreMotivation,
    deleteMotivationInStoreState,
    resetdDeleteMotivationInStoregState,
    resetAddUpdateMotivationInStoreState,
    getByIdMotivationInStoreState
  } = masterManagementStore;

  const { id, showDeletePopup, deletePopupToggleHandler } = props;
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);
 
  const storeId = getStoreIdFromLocalStorage();

  const callGetMotivationList = useCallback(() => {
    GetStoreMotivationByStoreId(storeId);
  }, [GetStoreMotivationByStoreId]);

  const deleteHandler = () => {
    DeleteStoreMotivation(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdateMotivationInStoreState();
  };

  const editHandler = (row: { Id: number }) => {
    const motiveId = row.Id;
    setEditableId(motiveId);
    AddStoreMotivation({
      MotiveId: motiveId,
      StoreId: storeId,
    });
  };

  useEffect(() => {
    callGetMotivationList();
  }, [callGetMotivationList]);

  useEffect(() => {
    if (addUpdateMotivationInStoreState.success) {
      toast.success(formatMessage("motivation_setting_add_success"));
      callGetMotivationList();
      resetAddUpdateMotivationInStoreState();
      setEditableId(-1);
    }
  }, [
    addUpdateMotivationInStoreState.success,
    callGetMotivationList,
    resetAddUpdateMotivationInStoreState,
    editableId,
  ]);

  useEffect(() => {
    if (deleteMotivationInStoreState.success) {
      resetdDeleteMotivationInStoregState();
      toast.success(formatMessage("motivation_setting_remove_success"));
      callGetMotivationList();
    }
  }, [
    deleteMotivationInStoreState.success,
    callGetMotivationList,
    resetdDeleteMotivationInStoregState,
  ]);

  return (
    <React.Fragment>

      {(addUpdateMotivationInStoreState.inProgress || deleteMotivationInStoreState.inProgress || getByIdMotivationInStoreState.inProgress) && (
        <FormLoader
          loading={addUpdateMotivationInStoreState.inProgress || deleteMotivationInStoreState.inProgress || getByIdMotivationInStoreState.inProgress}
        />
      )}

      {showDeletePopup && (
        <DeletePopup
          title="remove_motivation"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deleteMotivationInStoreState.inProgress}
          message="remove_motivation_confirm"
        />
      )}

      <MotivationInStoreListView
        id={id}
        showDeletePopup={showDeletePopup}
        deleteToggleHandler={deletePopupToggleHandler}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        editHandler={editHandler}
      />
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(MotivationInStore));
